import { useCallback, useEffect, useState } from "react";
import { api, errorMessage } from "../lib/api";
import { PlaceAreaInput } from "../components/PlaceAreaInput";
import { SearchAreaActions } from "../components/SearchAreaActions";
import { SearchMap } from "../components/SearchMap";
import { SearchFilterBar, SearchFiltersSheet } from "../components/SearchFiltersSheet";
import { SearchConsole } from "../components/SearchConsole";
import { ZoneEditorSheet } from "../components/ZoneEditorSheet";
import { SearchHistorySheet } from "../components/SearchHistorySheet";
import { SearchResultsSheet } from "../components/SearchResultsSheet";
import { VenueSheet } from "../components/VenueSheet";

export default function SearchPage({ session }) {
  const manager = session.user.role === "responsabile";
  const [key, setKey] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [tilesReady, setTilesReady] = useState(false);
  const [zones, setZones] = useState([]);
  const [venues, setVenues] = useState([]);
  const [groups, setGroups] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [circle, setCircle] = useState(null);
  const [focus, setFocus] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [confirmed, setConfirmed] = useState(null);
  const [step, setStep] = useState(1);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(10);
  const [minimumRating, setMinimumRating] = useState(0);
  const [drawing, setDrawing] = useState(false);
  const [points, setPoints] = useState([]);
  const [drawError, setDrawError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const [zoneSheetOpen, setZoneSheetOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [coverage, setCoverage] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [venueId, setVenueId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const selectedZone = zones.find((zone) => zone.id === selectedId);
  const areaLabel = selectedZone?.name || circle?.label || (points.length >= 3 ? "Area disegnata" : null);
  const csrf = { headers: { "X-CSRF-Token": session.csrf_token } };
  const loadZones = useCallback(() => api.get("/zones").then(({ data }) => setZones(data.items)), []);
  const loadHistory = useCallback(() => api.get("/searches").then(({ data }) => setHistory(data.items)), []);
  const loadCoverage = useCallback(() => api.get("/map/coverage").then(({ data }) => setCoverage(data.without_coordinates)), []);
  const loadVenues = useCallback(() => { if (bounds) api.get("/map/venues", { params: bounds })
    .then(({ data }) => setVenues(data.items)).catch((e) => setError(errorMessage(e))); }, [bounds]);
  useEffect(() => {
    Promise.all([api.get("/map/config"), api.get("/map/groups"), api.get("/venues/salespeople"),
      loadZones(), loadHistory(), loadCoverage()]).then(([config, configured, sales]) => {
      setKey(config.data.maps_key); setGroups(configured.data.groups); setPeople(sales.data);
    }).catch((e) => setError(errorMessage(e)));
  }, [loadZones, loadHistory, loadCoverage]);
  useEffect(() => { if (!bounds) return undefined; const timer = setTimeout(loadVenues, 220); return () => clearTimeout(timer); }, [bounds, loadVenues]);
  useEffect(() => {
    const refresh = () => api.get("/map/groups").then(({ data }) => { setGroups(data.groups);
      setKeywords((before) => before.filter((word) => data.groups.some((group) => group.keywords.includes(word)))); }).catch(() => {});
    window.addEventListener("luchador-groups-updated", refresh);
    return () => window.removeEventListener("luchador-groups-updated", refresh);
  }, []);
  useEffect(() => {
    if (!activeSearch?.id) return undefined;
    let live = true;
    const check = async () => {
      try {
        const { data } = await api.get(`/searches/${activeSearch.id}`);
        if (!live) return;
        if (data.status === "completed") { setActiveSearch(null); setResultId(data.id);
          loadHistory(); loadZones(); loadVenues(); loadCoverage(); }
        else if (data.status === "failed") { setActiveSearch(null); setError(data.error || "La ricerca non è riuscita. Riprova."); loadHistory(); }
        else setActiveSearch(data);
      } catch (e) { if (live) setError(errorMessage(e)); }
    };
    const timer = setInterval(check, 4500);
    return () => { live = false; clearInterval(timer); };
  }, [activeSearch?.id, loadHistory, loadZones, loadVenues, loadCoverage]);

  const changeArea = () => { setStep(1); setConfirmed(null); setFiltersOpen(false); setError(""); };
  const selectZone = useCallback((id) => { setSelectedId(id); setCircle(null); setPoints([]);
    setConfirmed(null); setInputKey((value) => value + 1); }, []);
  const choosePlace = ({ center, viewport, label }) => {
    const span = viewport?.high && viewport?.low ? Math.abs(viewport.high.latitude - viewport.low.latitude) : 0;
    const radius_km = span ? Math.max(2, Math.min(20, Math.round(span * 55))) : 5;
    setCircle({ center, radius_km, label }); setSelectedId(null); setPoints([]);
    setFocus({ center, zoom: radius_km <= 2 ? 14 : radius_km >= 12 ? 10 : 12, at: Date.now() });
    setConfirmed(null); setError("");
  };
  const startTrace = () => { setSelectedId(null); setCircle(null); setInputKey((value) => value + 1);
    setEditor(null); setPoints([]); setDrawing(true); setDrawError(""); setNotice(""); };
  const saveZone = (zone) => { setZoneSheetOpen(false); setDrawing(false); setPoints([]); setEditor(null);
    setSelectedId(zone.id); setNotice("Zona salvata."); loadZones(); };
  const confirmArea = async () => {
    const payload = selectedZone ? { zone_id: selectedId } : circle ? { area: { kind: "circle", label: circle.label,
      center: circle.center, radius_km: circle.radius_km } } : points.length >= 3 ? {
      area: { kind: "polygon", label: "Area disegnata", polygon: points } } : null;
    if (!payload) return;
    setBusy(true); setError("");
    try { const { data } = await api.post("/searches/areas/confirm", payload, csrf);
      setConfirmed(data); setStep(2); setDrawing(false); setFiltersOpen(true); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const launch = async () => {
    if (!confirmed) return;
    setBusy(true); setError(""); setNotice("");
    try { const { data } = await api.post("/searches", {
      area_id: confirmed.area_id, keywords, minimum_rating: minimumRating }, csrf);
      setFiltersOpen(false); setActiveSearch(data); loadHistory(); }
    catch (e) { setError(errorMessage(e)); if (e.response?.status === 409) changeArea(); }
    finally { setBusy(false); }
  };
  const chooseHistory = (item) => { setHistoryOpen(false); window.setTimeout(() => {
    if (item.status === "completed") setResultId(item.id);
    else if (item.status === "failed") setError(item.error || "La ricerca non è riuscita. Riprova.");
    else setActiveSearch(item);
  }, 180); };
  const enrich = async () => { setError("");
    try { const { data } = await api.post("/map/geocode-missing", {}, csrf);
      setNotice(`${data.queued} locali in aggiornamento.`);
      window.setTimeout(() => { loadCoverage(); loadVenues(); }, 12000);
    } catch (e) { setError(errorMessage(e)); } };
  const mapBounds = useCallback((next) => setBounds((previous) => previous &&
    Math.abs(previous.north - next.north) < .0001 && Math.abs(previous.west - next.west) < .0001 ? previous : next), []);
  const openResultVenue = (id) => { setResultId(null); window.setTimeout(() => setVenueId(id), 180); };

  return <div className="search-page" data-testid="page-cerca">
    {key ? <SearchMap mapsKey={key} zones={zones} venues={venues} selectedZoneId={selectedId} drawing={drawing} points={points}
      circle={circle} focus={focus} onCircleChange={setCircle}
      onBeginStroke={() => { setPoints([]); setDrawError(""); }} onCompleteStroke={(path) => { setPoints(path); setDrawError(""); }}
      onShortStroke={() => setDrawError("Traccia un'area più ampia con un unico movimento.")}
      onBounds={mapBounds} onZoom={setZoom} onReady={setMapReady} onTilesReady={setTilesReady}
      zoom={zoom} onSelectZone={selectZone} onOpenVenue={setVenueId}
      onError={() => setError("Google Maps non disponibile. Verifica la configurazione della mappa.")} />
      : <div className="map-loading" data-testid="map-loading">CARICAMENTO MAPPA…</div>}
    {key && !tilesReady && <div className="map-tiles-loading" role="status" data-testid="map-tiles-loading">CARICAMENTO MAPPA…</div>}
    <div className="map-overlay-top">{step === 1 ? drawing ? null : <PlaceAreaInput key={inputKey} csrfToken={session.csrf_token}
      onChoose={choosePlace} onTyping={() => { setCircle(null); setSelectedId(null); setConfirmed(null); }} />
      : <SearchFilterBar selectedZone={{ name: confirmed?.label }} keywords={keywords}
        minimumRating={minimumRating} onOpen={() => setFiltersOpen(true)} />}</div>
    {activeSearch && <div className="map-radar" role="status" data-testid="search-radar"><div className="radar-sweep" aria-hidden="true" /><span>{activeSearch.status === "queued" ? "RICERCA IN CODA" : "EL SABUESO STA CERCANDO"}</span></div>}
    {error && <p className="map-flash error" role="alert" data-testid="search-error">{error}</p>}
    {notice && <p className="map-flash" role="status" data-testid="search-notice">{notice}</p>}
    {step === 1 ? <SearchAreaActions circle={circle} areaLabel={areaLabel} drawing={drawing}
      pointsReady={points.length >= 3} drawError={drawError} manager={manager} radius={circle?.radius_km}
      onRadius={(radius_km) => setCircle((before) => ({ ...before, radius_km }))}
      onDraw={startTrace} onCancelDraw={() => { setDrawing(false); setPoints([]); setDrawError(""); }}
      onSaveZone={() => { setDrawing(false); setZoneSheetOpen(true); }} onConfirm={confirmArea} confirming={busy}
      onHistory={() => setHistoryOpen(true)} onEdit={selectedZone && manager ? () => { setEditor(selectedZone); setPoints([]); setZoneSheetOpen(true); } : null} />
      : <SearchConsole keywords={keywords} onSearch={launch} searching={busy || Boolean(activeSearch)}
        onHistory={() => setHistoryOpen(true)} onBack={changeArea} />}
    <SearchFiltersSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} groups={groups} keywords={keywords}
      onKeywords={setKeywords} minimumRating={minimumRating} onRating={setMinimumRating}
      areaLabel={confirmed?.label} coverage={coverage} manager={manager} onEnrich={() => { setFiltersOpen(false); enrich(); }}
      onBack={changeArea} onSearch={launch} searching={busy || Boolean(activeSearch)} />
    <ZoneEditorSheet open={zoneSheetOpen} zone={editor} polygon={points} people={people} session={session}
      onClose={() => { setZoneSheetOpen(false); setPoints([]); setEditor(null); }} onSaved={saveZone}
      onRedraw={() => { setZoneSheetOpen(false); setPoints([]); setDrawError(""); setDrawing(true); }} />
    <SearchHistorySheet open={historyOpen} items={history} onClose={() => setHistoryOpen(false)} onSelect={chooseHistory} />
    <SearchResultsSheet searchId={resultId} session={session} onClose={() => setResultId(null)}
      onUpdated={() => { loadHistory(); loadVenues(); }} onOpenVenue={openResultVenue} />
    <VenueSheet venueId={venueId} session={session} bookingMode={false} onClose={() => setVenueId(null)} onChanged={loadVenues} />
  </div>;
}